import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  CircularProgress,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import DataTable, {
  ColumnDef,
  SortOrder,
} from "../../../../components/DataTable/DataTable";
import { AssignmentVersionGridRow } from "../../../../services/api/types/assignments/assignment";
import { brand, gray } from "../../../../theme/themePrimitives";
import { REMOVETEXT } from "../../../../utils/constants";

interface VersionHistoryGridProps {
  rows: AssignmentVersionGridRow[];
  loading?: boolean;
  onRemove: (row: AssignmentVersionGridRow) => void;
  /** Opens the Edit version modal for the clicked version link. */
  onEditVersion?: (row: AssignmentVersionGridRow) => void;
  onUploadNewVersion?: () => void;
  uploadNewVersionLoading?: boolean;
  /** Only System Admins can remove versions; hides the Remove column otherwise. */
  canRemoveVersions?: boolean;
}

export const VersionHistoryGrid: React.FC<VersionHistoryGridProps> = ({
  rows,
  loading = false,
  onRemove,
  onEditVersion,
  onUploadNewVersion,
  uploadNewVersionLoading = false,
  canRemoveVersions = false,
}) => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sort, setSort] = useState<{ orderBy: string; order: SortOrder }>();

  const canRemove = (row: AssignmentVersionGridRow) =>
    (row.usersAssigned == null || row.usersAssigned === 0) && rows.length > 1;

  // Sorting and pagination are client-side — the full list arrives with the
  // assignment. creationDate holds the raw ISO date so it sorts correctly.
  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const dir = sort.order === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sort.orderBy as keyof AssignmentVersionGridRow];
      const bv = b[sort.orderBy as keyof AssignmentVersionGridRow];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, sort]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  const columns: ColumnDef<AssignmentVersionGridRow>[] = [
    {
      id: "packageName",
      label: "Name",
      sortable: true,
      getValue: (r) => r.packageName || "—",
    },
    {
      id: "version",
      label: "Version",
      sortable: true,
      render: (r) =>
        onEditVersion ? (
          <Link
            component="button"
            underline="always"
            onClick={() => onEditVersion(r)}
            sx={{ color: brand[600], cursor: "pointer" }}
          >
            {r.version || "—"}
          </Link>
        ) : (
          r.version || "—"
        ),
    },
    {
      id: "note",
      label: "Note",
      sortable: true,
      getValue: (r) => r.note || "—",
    },
    {
      id: "usersAssigned",
      label: "User assigned",
      sortable: true,
      getValue: (r) => r.usersAssigned ?? "—",
    },
    {
      id: "creationDate",
      label: "Creation date",
      sortable: true,
      render: (r) =>
        r.creationDate ? dayjs(r.creationDate).format("DD-MMM-YYYY") : "—",
    },
    ...(canRemoveVersions
      ? [
        {
          id: "remove",
          label: "",
          align: "right" as const,
          render: (r: AssignmentVersionGridRow) => {
            const enabled = canRemove(r);
            const button = (
              <Button
                variant="text"
                disabled={!enabled}
                onClick={() => onRemove(r)}
                sx={{
                  minWidth: "auto",
                  p: 0,
                  color: enabled ? "black" : gray[400],
                  "&.Mui-disabled": { color: gray[400] },
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                {REMOVETEXT}
              </Button>
            );
            return enabled ? (
              button
            ) : (
              <Tooltip
                title={
                  (r.usersAssigned ?? 0) > 0
                    ? "This version has assigned users and cannot be removed"
                    : "The only version cannot be removed"
                }
                arrow
                placement="left"
              >
                <span>{button}</span>
              </Tooltip>
            );
          },
        },
      ]
      : []),
  ];

  return (
    <Box sx={{
      border: "1px solid #EAECF0",
      boxShadow: "0px 1px 3px 0px #1018281A",
      borderRadius: "12px",
      pt: 2
    }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
          p: "8px 16px 24px 16px",
          borderBottom: "1px solid #EAECF0"
        }}
      >
        <Typography variant="h6">Version history</Typography>
        {onUploadNewVersion && (
          <Button
            variant="outlined"
            startIcon={
              uploadNewVersionLoading ? (
                <CircularProgress size={16} />
              ) : (
                <AddIcon />
              )
            }
            disabled={uploadNewVersionLoading}
            onClick={onUploadNewVersion}
            sx={{
              border: `1px solid ${gray[300]}`,
              borderRadius: "8px",
              color: gray[800],
              backgroundColor: "white",
              "&:hover": { backgroundColor: gray[50] },
            }}
          >
            Upload new version
          </Button>
        )}
      </Box>
      <DataTable<AssignmentVersionGridRow>
        data={pagedRows}
        columns={columns}
        totalCount={rows.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(rows) => {
          setRowsPerPage(rows);
          setPage(1);
        }}
        rowsPerPageOptionsOverride={[5, 10, 25]}
        sort={sort}
        onSortChange={(orderBy, order) => setSort({ orderBy, order })}
        isLoading={loading}
        disablePaper
        getRowId={(row) => row.assignmentVersionUid}
        renderEmptyState={
          <Typography variant="body2" color="text.secondary">
            No versions uploaded yet.
          </Typography>
        }
      />
    </Box>
  );
};
